import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '../../components/ui/Card';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Table, type TableColumn } from '../../components/ui/Table';
import { Pagination } from '../../components/ui/Pagination';
import { Modal } from '../../components/ui/Modal';
import { ConfirmationModal } from '../../components/ui/ConfirmationModal';
import { Select } from '../../components/ui/Select';
import { Tabs } from '../../components/ui/Tabs';
import api from '../../services/api';

interface NewsletterSubscription {
  id: number;
  email: string;
  user_id: number | null;
  account_id: number | null;
  status: 'active' | 'unsubscribed';
  source: string;
  subscribed_at: string;
  unsubscribed_at: string | null;
  user?: { name: string; email: string };
  account?: { subscription_plan?: { name: string; price: number } };
}

interface NewsletterStats {
  total: number;
  active: number;
  unsubscribed: number;
  free_subscribers: number;
  paid_subscribers: number;
  guest_subscribers: number;
  today: number;
  this_week: number;
  this_month: number;
}

interface NewsletterTemplate {
  id: number;
  name: string;
  subject: string;
  body: string;
  created_at: string;
  updated_at: string;
}

// ─── Templates Tab ────────────────────────────────────────────────────────────

const emptyTemplate = { name: '', subject: '', body: '' };

const TemplatesTab: React.FC<{
  onUseTemplate: (t: NewsletterTemplate) => void;
}> = ({ onUseTemplate }) => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<NewsletterTemplate | null>(null);
  const [form, setForm] = useState(emptyTemplate);
  const [deleteTarget, setDeleteTarget] = useState<NewsletterTemplate | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<NewsletterTemplate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const { data: templates = [], isLoading } = useQuery<NewsletterTemplate[]>({
    queryKey: ['newsletter-templates'],
    queryFn: async () => (await api.get('/api/admin/newsletter/templates')).data,
  });

  const saveMutation = useMutation({
    mutationFn: (data: typeof emptyTemplate) =>
      editing
        ? api.put(`/api/admin/newsletter/templates/${editing.id}`, data)
        : api.post('/api/admin/newsletter/templates', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsletter-templates'] });
      setSuccess(editing ? 'Template updated' : 'Template created');
      setIsModalOpen(false);
      setEditing(null);
      setForm(emptyTemplate);
    },
    onError: (err: any) => setError(err.response?.data?.message || 'Failed to save template'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/api/admin/newsletter/templates/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsletter-templates'] });
      setSuccess('Template deleted');
      setDeleteTarget(null);
    },
    onError: (err: any) => setError(err.response?.data?.message || 'Failed to delete template'),
  });

  useEffect(() => {
    if (success || error) {
      const t = setTimeout(() => { setSuccess(null); setError(null); }, 4000);
      return () => clearTimeout(t);
    }
  }, [success, error]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyTemplate);
    setIsModalOpen(true);
  };

  const openEdit = (t: NewsletterTemplate) => {
    setEditing(t);
    setForm({ name: t.name, subject: t.subject, body: t.body });
    setIsModalOpen(true);
  };

  const handleSave = () => {
    if (!form.name || !form.subject || !form.body) {
      setError('All fields are required');
      return;
    }
    saveMutation.mutate(form);
  };

  return (
    <div className="space-y-4">
      {success && <Alert variant="success" onClose={() => setSuccess(null)}>{success}</Alert>}
      {error && <Alert variant="error" onClose={() => setError(null)}>{error}</Alert>}

      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">
          Create reusable email templates. Use them when sending campaigns.
        </p>
        <Button onClick={openCreate}>Create Template</Button>
      </div>

      {isLoading ? (
        <div className="text-center py-10">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
      ) : templates.length === 0 ? (
        <Card className="py-16 text-center">
          <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <p className="text-gray-500 font-medium">No templates yet</p>
          <p className="text-sm text-gray-400 mt-1">Create your first template to reuse it across campaigns.</p>
          <Button className="mt-4" onClick={openCreate}>Create Template</Button>
        </Card>
      ) : (
        <div className="grid gap-4">
          {templates.map(t => (
            <Card key={t.id} className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-gray-900 truncate">{t.name}</h3>
                    <span className="text-xs text-gray-400 shrink-0">
                      {new Date(t.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    <span className="font-medium text-gray-700">Subject:</span> {t.subject}
                  </p>
                  <p className="text-sm text-gray-400 line-clamp-2"
                    dangerouslySetInnerHTML={{ __html: t.body.replace(/<[^>]+>/g, ' ').slice(0, 180) + '…' }}
                  />
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="primary" onClick={() => onUseTemplate(t)}>
                    Use
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => setPreviewTemplate(t)}>
                    Preview
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => openEdit(t)}>
                    Edit
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => setDeleteTarget(t)}>
                    Delete
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit Modal */}
      <Modal
        open={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditing(null); }}
        title={editing ? 'Edit Template' : 'Create Template'}
        size="large"
      >
        <div className="space-y-4">
          <Input
            label="Template Name"
            placeholder="e.g. May Marketing Campaign"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            required
          />
          <Input
            label="Subject Line"
            placeholder="Email subject shown to recipients"
            value={form.subject}
            onChange={e => setForm({ ...form, subject: e.target.value })}
            required
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Body <span className="text-gray-400 font-normal">(HTML supported)</span>
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
              rows={14}
              value={form.body}
              onChange={e => setForm({ ...form, body: e.target.value })}
              placeholder="Write your email body here. HTML is supported."
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => { setIsModalOpen(false); setEditing(null); }}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? 'Saving…' : editing ? 'Save Changes' : 'Create Template'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Preview Modal */}
      <Modal
        open={!!previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        title={previewTemplate ? `Preview: ${previewTemplate.name}` : ''}
        size="large"
      >
        {previewTemplate && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-md border border-gray-200">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Subject:</span>
              <span className="text-sm text-gray-800">{previewTemplate.subject}</span>
            </div>
            <iframe
              srcDoc={previewTemplate.body}
              title="Email preview"
              className="w-full rounded-md border border-gray-200"
              style={{ height: '520px' }}
            />
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="secondary" onClick={() => setPreviewTemplate(null)}>Close</Button>
              <Button onClick={() => { onUseTemplate(previewTemplate); setPreviewTemplate(null); }}>
                Use This Template
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete confirmation */}
      <ConfirmationModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        title="Delete Template"
        message={`Delete "${deleteTarget?.name}"? This cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
};

// ─── Subscribers Tab ──────────────────────────────────────────────────────────

const SubscribersTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'unsubscribed'>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'free' | 'paid' | 'guest'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isSendEmailModalOpen, setIsSendEmailModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<NewsletterSubscription | null>(null);

  const [emailForm, setEmailForm] = useState({ recipient_type: 'all', subject: '', message: '', campaign_ref: '' });

  // Templates for the "load template" dropdown
  const { data: templates = [] } = useQuery<NewsletterTemplate[]>({
    queryKey: ['newsletter-templates'],
    queryFn: async () => (await api.get('/api/admin/newsletter/templates')).data,
  });

  const { data: subscriptionsData, isLoading } = useQuery({
    queryKey: ['admin-newsletter-subscriptions', page, statusFilter, typeFilter, searchTerm],
    queryFn: async () => {
      const params: any = { page, per_page: 20 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.type = typeFilter;
      if (searchTerm) params.search = searchTerm;
      return (await api.get('/api/admin/newsletter', { params })).data;
    },
  });

  const { data: stats } = useQuery<NewsletterStats>({
    queryKey: ['admin-newsletter-stats'],
    queryFn: async () => (await api.get('/api/admin/newsletter/statistics')).data,
  });

  const currentPageIds = useMemo(
    () => (subscriptionsData?.data || []).map((s: NewsletterSubscription) => s.id),
    [subscriptionsData]
  );
  const allPageSelected = currentPageIds.length > 0 && currentPageIds.every((id: number) => selectedIds.has(id));
  const somePageSelected = currentPageIds.some((id: number) => selectedIds.has(id));

  useEffect(() => { setSelectedIds(new Set()); }, [statusFilter, typeFilter, searchTerm, page]);

  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allPageSelected) currentPageIds.forEach((id: number) => next.delete(id));
      else currentPageIds.forEach((id: number) => next.add(id));
      return next;
    });
  };

  const toggleSelectOne = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  useEffect(() => {
    if (success || error) {
      const t = setTimeout(() => { setSuccess(null); setError(null); }, 5000);
      return () => clearTimeout(t);
    }
  }, [success, error]);

  const toggleStatusMutation = useMutation({
    mutationFn: (id: number) => api.post(`/api/admin/newsletter/${id}/toggle-status`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-newsletter-subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['admin-newsletter-stats'] });
      setSuccess('Subscription status updated');
    },
    onError: (err: any) => setError(err.response?.data?.message || 'Failed to update status'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/api/admin/newsletter/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-newsletter-subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['admin-newsletter-stats'] });
      setSuccess('Subscription removed');
      setIsDeleteModalOpen(false);
      setSelectedSubscription(null);
    },
    onError: (err: any) => setError(err.response?.data?.message || 'Failed to remove subscription'),
  });

  const sendEmailMutation = useMutation({
    mutationFn: (data: any) => api.post('/api/admin/newsletter/send-email', data),
    onSuccess: (response) => {
      setSuccess(`Email sent to ${response.data.recipients_count} subscribers`);
      setIsSendEmailModalOpen(false);
      setEmailForm({ recipient_type: 'all', subject: '', message: '', campaign_ref: '' });
      setSelectedIds(new Set());
    },
    onError: (err: any) => setError(err.response?.data?.message || 'Failed to send email'),
  });

  const exportMutation = useMutation({
    mutationFn: async (type: string) => (await api.get('/api/admin/newsletter/export', { params: { type } })).data,
    onSuccess: (data) => {
      const csv = data.data.length === 0 ? '' :
        Object.keys(data.data[0]).join(',') + '\n' +
        data.data.map((obj: any) => Object.values(obj).map((v: any) => `"${v}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `newsletter-${data.count}.csv`; a.click();
      window.URL.revokeObjectURL(url);
      setSuccess(`Exported ${data.count} subscribers`);
    },
    onError: (err: any) => setError(err.response?.data?.message || 'Failed to export'),
  });

  const handleSendEmail = () => {
    if (!emailForm.subject || !emailForm.message) { setError('Please fill in all fields'); return; }
    const payload: any = { ...emailForm };
    if (emailForm.recipient_type === 'selected') payload.recipient_ids = [...selectedIds];
    sendEmailMutation.mutate(payload);
  };

  const loadTemplate = (templateId: string) => {
    const t = templates.find(t => String(t.id) === templateId);
    if (t) setEmailForm(prev => ({ ...prev, subject: t.subject, message: t.body }));
  };

  const openSendModal = (preselect?: 'selected') => {
    setEmailForm({ recipient_type: preselect || 'all', subject: '', message: '' });
    setIsSendEmailModalOpen(true);
  };

  const getSubscriberType = (row: NewsletterSubscription) => {
    if (!row.account_id) return 'Guest';
    if (row.account?.subscription_plan && row.account.subscription_plan.price > 0) return 'Paid';
    return 'Free';
  };

  const columns: TableColumn<NewsletterSubscription>[] = [
    {
      key: 'id',
      header: '',
      render: (_, row) => row ? (
        <input type="checkbox" className="w-4 h-4 rounded border-gray-300 text-indigo-600 cursor-pointer"
          checked={selectedIds.has(row.id)} onChange={() => toggleSelectOne(row.id)}
          onClick={e => e.stopPropagation()} />
      ) : null,
    },
    {
      key: 'email',
      header: 'Email',
      render: (_, row) => row ? (
        <div>
          <div className="font-medium text-gray-900">{row.email}</div>
          {row.user && <div className="text-sm text-gray-500">{row.user.name}</div>}
        </div>
      ) : <div className="text-gray-400">-</div>,
    },
    {
      key: 'account_id',
      header: 'Type',
      render: (_, row) => {
        if (!row) return <Badge variant="secondary" size="sm">Unknown</Badge>;
        const type = getSubscriberType(row);
        return <Badge variant={type === 'Paid' ? 'success' : type === 'Free' ? 'warning' : 'secondary'} size="sm">{type}</Badge>;
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (_, row) => row ? (
        <Badge variant={row.status === 'active' ? 'success' : 'secondary'} size="sm">{row.status}</Badge>
      ) : <Badge variant="secondary" size="sm">Unknown</Badge>,
    },
    {
      key: 'source',
      header: 'Source',
      render: (_, row) => <span className="text-sm text-gray-600 capitalize">{row?.source ?? '-'}</span>,
    },
    {
      key: 'subscribed_at',
      header: 'Subscribed',
      render: (_, row) => row ? (
        <span className="text-sm text-gray-600">
          {new Date(row.subscribed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </span>
      ) : <span className="text-sm text-gray-400">-</span>,
    },
    {
      key: 'unsubscribed_at',
      header: 'Actions',
      render: (_, row) => row ? (
        <div className="flex gap-2">
          <Button size="sm" variant={row.status === 'active' ? 'secondary' : 'primary'}
            onClick={() => toggleStatusMutation.mutate(row.id)}>
            {row.status === 'active' ? 'Deactivate' : 'Reactivate'}
          </Button>
          <Button size="sm" variant="danger" onClick={() => { setSelectedSubscription(row); setIsDeleteModalOpen(true); }}>
            Remove
          </Button>
        </div>
      ) : <div>-</div>,
    },
  ];

  const recipientOptions = [
    { value: 'all', label: `All Active Subscribers (${stats?.active || 0})` },
    { value: 'paid', label: `Paid Only (${stats?.paid_subscribers || 0})` },
    { value: 'free', label: `Free Only (${stats?.free_subscribers || 0})` },
    { value: 'guest', label: `Guest Only (${stats?.guest_subscribers || 0})` },
    ...(selectedIds.size > 0 ? [{ value: 'selected', label: `Selected Only (${selectedIds.size})` }] : []),
  ];

  return (
    <div className="space-y-6">
      {success && <Alert variant="success" onClose={() => setSuccess(null)}>{success}</Alert>}
      {error && <Alert variant="error" onClose={() => setError(null)}>{error}</Alert>}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Subscribers</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">{stats.active} active, {stats.unsubscribed} unsubscribed</p>
          </Card>
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Paid</p>
                <p className="text-2xl font-bold text-green-600">{stats.paid_subscribers.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Active paying customers</p>
          </Card>
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Free</p>
                <p className="text-2xl font-bold text-orange-600">{stats.free_subscribers.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Free plan or trial users</p>
          </Card>
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Guest</p>
                <p className="text-2xl font-bold text-purple-600">{stats.guest_subscribers.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">No account registered</p>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input label="Search" placeholder="Search by email…" value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setPage(1); }} />
          <Select label="Status" value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value as any); setPage(1); }}
            options={[
              { value: 'all', label: 'All Statuses' },
              { value: 'active', label: 'Active' },
              { value: 'unsubscribed', label: 'Unsubscribed' },
            ]} />
          <Select label="Type" value={typeFilter}
            onChange={e => { setTypeFilter(e.target.value as any); setPage(1); }}
            options={[
              { value: 'all', label: 'All Types' },
              { value: 'paid', label: 'Paid Only' },
              { value: 'free', label: 'Free Only' },
              { value: 'guest', label: 'Guest Only' },
            ]} />
        </div>
      </Card>

      {/* Table */}
      <Card>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" className="w-4 h-4 rounded border-gray-300 text-indigo-600"
              checked={allPageSelected}
              ref={el => { if (el) el.indeterminate = somePageSelected && !allPageSelected; }}
              onChange={toggleSelectAll} />
            <span className="text-sm text-gray-600">
              {allPageSelected ? 'Deselect all on page' : `Select all on page (${currentPageIds.length})`}
            </span>
          </label>

          <div className="flex items-center gap-2">
            {selectedIds.size > 0 && (
              <>
                <span className="text-sm font-medium text-indigo-700 bg-indigo-50 px-3 py-1 rounded-full">
                  {selectedIds.size} selected
                </span>
                <Button size="sm" onClick={() => openSendModal('selected')}>
                  Send to Selected
                </Button>
                <button className="text-sm text-gray-500 hover:text-gray-700"
                  onClick={() => setSelectedIds(new Set())}>
                  Clear
                </button>
              </>
            )}
            <Button variant="secondary" size="sm" onClick={() => exportMutation.mutate(typeFilter)}>
              Export
            </Button>
            <Button size="sm" onClick={() => openSendModal()}>
              Send Email
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
            <p className="text-gray-600 mt-2">Loading…</p>
          </div>
        ) : (
          <>
            <Table columns={columns} data={subscriptionsData?.data || []} />
            {subscriptionsData && subscriptionsData.total > subscriptionsData.per_page && (
              <div className="mt-4">
                <Pagination currentPage={page} totalPages={subscriptionsData.last_page} onPageChange={setPage} />
              </div>
            )}
          </>
        )}
      </Card>

      {/* Send Email Modal */}
      <Modal open={isSendEmailModalOpen} onClose={() => setIsSendEmailModalOpen(false)}
        title="Send Email to Subscribers" size="large">
        <div className="space-y-4">
          <Select label="Recipients" value={emailForm.recipient_type}
            onChange={e => setEmailForm({ ...emailForm, recipient_type: e.target.value })}
            options={recipientOptions} />

          {/* Load Template */}
          {templates.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Load Template</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                defaultValue=""
                onChange={e => { if (e.target.value) loadTemplate(e.target.value); }}
              >
                <option value="">— select a template —</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">Loads the template's subject and body into the fields below.</p>
            </div>
          )}

          <Input
            label="Campaign Name (for analytics)"
            placeholder="e.g. may-2026-features"
            value={emailForm.campaign_ref}
            onChange={e => setEmailForm({ ...emailForm, campaign_ref: e.target.value })}
          />

          <Input label="Subject" value={emailForm.subject}
            onChange={e => setEmailForm({ ...emailForm, subject: e.target.value })} required />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message <span className="text-gray-400 font-normal">(HTML supported)</span>
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              rows={10} value={emailForm.message}
              onChange={e => setEmailForm({ ...emailForm, message: e.target.value })} />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setIsSendEmailModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSendEmail} disabled={sendEmailMutation.isPending}>
              {sendEmailMutation.isPending ? 'Sending…' : 'Send Email'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmationModal
        open={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setSelectedSubscription(null); }}
        onConfirm={() => { if (selectedSubscription) deleteMutation.mutate(selectedSubscription.id); }}
        title="Remove Subscription"
        message={`Remove "${selectedSubscription?.email}" from the newsletter list?`}
        confirmText="Remove"
        variant="danger"
      />
    </div>
  );
};

// ─── Platform Users Tab ───────────────────────────────────────────────────────

interface PlatformUser {
  id: number;
  name: string;
  email: string;
  created_at: string;
  is_unsubscribed: boolean;
  account?: {
    subscription_plan?: { name: string; price: number };
  };
}

const PlatformUsersTab: React.FC = () => {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ subject: '', message: '', campaign_ref: '' });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const { data: templates = [] } = useQuery<NewsletterTemplate[]>({
    queryKey: ['newsletter-templates'],
    queryFn: async () => (await api.get('/api/admin/newsletter/templates')).data,
  });

  const { data: usersData, isLoading } = useQuery({
    queryKey: ['newsletter-platform-users', page, searchTerm],
    queryFn: async () => {
      const params: any = { page, per_page: 20 };
      if (searchTerm) params.search = searchTerm;
      return (await api.get('/api/admin/newsletter/platform-users', { params })).data;
    },
  });

  const currentPageIds: number[] = useMemo(
    () => (usersData?.data || [])
      .filter((u: PlatformUser) => !u.is_unsubscribed)
      .map((u: PlatformUser) => u.id),
    [usersData]
  );

  const allPageSelected = currentPageIds.length > 0 && currentPageIds.every(id => selectedIds.has(id));
  const somePageSelected = currentPageIds.some(id => selectedIds.has(id));

  useEffect(() => { setSelectedIds(new Set()); }, [searchTerm, page]);

  useEffect(() => {
    if (success || error) {
      const t = setTimeout(() => { setSuccess(null); setError(null); }, 5000);
      return () => clearTimeout(t);
    }
  }, [success, error]);

  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allPageSelected) currentPageIds.forEach(id => next.delete(id));
      else currentPageIds.forEach(id => next.add(id));
      return next;
    });
  };

  const toggleOne = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const sendMutation = useMutation({
    mutationFn: (data: any) => api.post('/api/admin/newsletter/send-to-users', data),
    onSuccess: (res) => {
      setSuccess(`Email sent to ${res.data.recipients_count} users`);
      setIsSendModalOpen(false);
      setEmailForm({ subject: '', message: '', campaign_ref: '' });
      setSelectedIds(new Set());
    },
    onError: (err: any) => setError(err.response?.data?.message || 'Failed to send email'),
  });

  const handleSend = () => {
    if (!emailForm.subject || !emailForm.message) { setError('Please fill in all fields'); return; }
    if (selectedIds.size === 0) { setError('No users selected'); return; }
    sendMutation.mutate({ user_ids: [...selectedIds], ...emailForm });
  };

  const loadTemplate = (templateId: string) => {
    const t = templates.find(t => String(t.id) === templateId);
    if (t) setEmailForm({ subject: t.subject, message: t.body });
  };

  const columns: TableColumn<PlatformUser>[] = [
    {
      key: 'id',
      header: '',
      render: (_, row) => {
        if (!row) return null;
        if (row.is_unsubscribed) return null;
        return (
          <input type="checkbox" className="w-4 h-4 rounded border-gray-300 text-indigo-600 cursor-pointer"
            checked={selectedIds.has(row.id)} onChange={() => toggleOne(row.id)}
            onClick={e => e.stopPropagation()} />
        );
      },
    },
    {
      key: 'name',
      header: 'User',
      render: (_, row) => row ? (
        <div>
          <div className="font-medium text-gray-900">{row.name}</div>
          <div className="text-sm text-gray-500">{row.email}</div>
        </div>
      ) : null,
    },
    {
      key: 'account',
      header: 'Plan',
      render: (_, row) => row ? (
        <Badge variant="warning" size="sm">
          {row.account?.subscription_plan?.name ?? 'Free Trial'}
        </Badge>
      ) : null,
    },
    {
      key: 'is_unsubscribed',
      header: 'Email Status',
      render: (_, row) => row ? (
        row.is_unsubscribed
          ? <Badge variant="secondary" size="sm">Unsubscribed</Badge>
          : <Badge variant="success" size="sm">Reachable</Badge>
      ) : null,
    },
    {
      key: 'created_at',
      header: 'Registered',
      render: (_, row) => row ? (
        <span className="text-sm text-gray-600">
          {new Date(row.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </span>
      ) : null,
    },
  ];

  const reachableTotal = (usersData?.data || []).filter((u: PlatformUser) => !u.is_unsubscribed).length;

  return (
    <div className="space-y-6">
      {success && <Alert variant="success" onClose={() => setSuccess(null)}>{success}</Alert>}
      {error && <Alert variant="error" onClose={() => setError(null)}>{error}</Alert>}

      {/* Info banner */}
      <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <svg className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-sm text-amber-800">
          These are registered platform users on the <strong>Free Trial</strong> plan who have not explicitly unsubscribed.
          They did not opt in to a newsletter, so use this sparingly for product updates.
        </p>
      </div>

      {/* Search + action bar */}
      <Card>
        <div className="flex items-end gap-4">
          <div className="flex-1">
            <Input label="Search" placeholder="Search by name or email…" value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setPage(1); }} />
          </div>
          {usersData && (
            <p className="text-sm text-gray-500 pb-2 shrink-0">
              {usersData.total} users · {reachableTotal} reachable on this page
            </p>
          )}
        </div>
      </Card>

      {/* Table */}
      <Card>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" className="w-4 h-4 rounded border-gray-300 text-indigo-600"
              checked={allPageSelected}
              ref={el => { if (el) el.indeterminate = somePageSelected && !allPageSelected; }}
              onChange={toggleSelectAll} />
            <span className="text-sm text-gray-600">
              {allPageSelected ? 'Deselect all on page' : `Select all reachable on page (${currentPageIds.length})`}
            </span>
          </label>

          <div className="flex items-center gap-2">
            {selectedIds.size > 0 && (
              <>
                <span className="text-sm font-medium text-indigo-700 bg-indigo-50 px-3 py-1 rounded-full">
                  {selectedIds.size} selected
                </span>
                <Button size="sm" onClick={() => setIsSendModalOpen(true)}>
                  Send Email to Selected
                </Button>
                <button className="text-sm text-gray-500 hover:text-gray-700"
                  onClick={() => setSelectedIds(new Set())}>
                  Clear
                </button>
              </>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
            <p className="text-gray-600 mt-2">Loading users…</p>
          </div>
        ) : (
          <>
            <Table columns={columns} data={usersData?.data || []} />
            {usersData && usersData.total > usersData.per_page && (
              <div className="mt-4">
                <Pagination currentPage={page} totalPages={usersData.last_page} onPageChange={setPage} />
              </div>
            )}
          </>
        )}
      </Card>

      {/* Send Modal */}
      <Modal open={isSendModalOpen} onClose={() => setIsSendModalOpen(false)}
        title={`Send Email to ${selectedIds.size} Selected Users`} size="large">
        <div className="space-y-4">
          {templates.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Load Template</label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                defaultValue="" onChange={e => { if (e.target.value) loadTemplate(e.target.value); }}>
                <option value="">— select a template —</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <p className="text-xs text-gray-400 mt-1">Loads subject and body from the selected template.</p>
            </div>
          )}

          <Input
            label="Campaign Name (for analytics)"
            placeholder="e.g. may-2026-features"
            value={emailForm.campaign_ref}
            onChange={e => setEmailForm({ ...emailForm, campaign_ref: e.target.value })}
          />

          <Input label="Subject" value={emailForm.subject}
            onChange={e => setEmailForm({ ...emailForm, subject: e.target.value })} required />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message <span className="text-gray-400 font-normal">(HTML supported)</span>
            </label>
            <textarea className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              rows={10} value={emailForm.message}
              onChange={e => setEmailForm({ ...emailForm, message: e.target.value })} />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setIsSendModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSend} disabled={sendMutation.isPending}>
              {sendMutation.isPending ? 'Sending…' : `Send to ${selectedIds.size} Users`}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// ─── Click Analytics Tab ──────────────────────────────────────────────────────

interface ClickLink { original_url: string; clicks: number }
interface CampaignStat {
  campaign_ref: string;
  total_links: number;
  total_clicks: number;
  unique_clickers: number;
  click_rate: number;
  links: ClickLink[];
}

const ClickAnalyticsTab: React.FC = () => {
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data: stats = [], isLoading } = useQuery<CampaignStat[]>({
    queryKey: ['newsletter-click-stats'],
    queryFn: async () => (await api.get('/api/admin/newsletter/click-stats')).data,
    refetchInterval: 30000,
  });

  if (isLoading) return (
    <div className="text-center py-12">
      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
    </div>
  );

  if (stats.length === 0) return (
    <Card className="py-16 text-center">
      <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      </div>
      <p className="text-gray-500 font-medium">No click data yet</p>
      <p className="text-sm text-gray-400 mt-1">Click data will appear here once you send a campaign.</p>
    </Card>
  );

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">Click data refreshes every 30 seconds.</p>

      {stats.map(campaign => (
        <Card key={campaign.campaign_ref} className="overflow-hidden">
          {/* Campaign header row */}
          <button
            className="w-full text-left px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            onClick={() => setExpanded(expanded === campaign.campaign_ref ? null : campaign.campaign_ref)}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-2 h-2 rounded-full bg-indigo-500 shrink-0" />
              <span className="font-semibold text-gray-900 truncate">{campaign.campaign_ref}</span>
            </div>

            <div className="flex items-center gap-6 shrink-0 ml-4">
              <div className="text-center">
                <p className="text-xs text-gray-400">Links wrapped</p>
                <p className="text-lg font-bold text-gray-900">{campaign.total_links}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-400">Total clicks</p>
                <p className="text-lg font-bold text-indigo-600">{campaign.total_clicks}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-400">Unique clickers</p>
                <p className="text-lg font-bold text-purple-600">{campaign.unique_clickers}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-400">Click rate</p>
                <p className="text-lg font-bold text-green-600">{campaign.click_rate}%</p>
              </div>
              <svg
                className={`w-5 h-5 text-gray-400 transition-transform ${expanded === campaign.campaign_ref ? 'rotate-180' : ''}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>

          {/* Expanded: per-link breakdown */}
          {expanded === campaign.campaign_ref && (
            <div className="border-t border-gray-100 px-6 py-4">
              {campaign.links.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">No clicks recorded yet for this campaign.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-2 text-gray-500 font-medium">Link</th>
                      <th className="text-right py-2 text-gray-500 font-medium w-24">Clicks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaign.links.map((link, i) => (
                      <tr key={i} className="border-b border-gray-50 last:border-0">
                        <td className="py-2 pr-4">
                          <a href={link.original_url} target="_blank" rel="noopener noreferrer"
                            className="text-indigo-600 hover:underline break-all">
                            {link.original_url}
                          </a>
                        </td>
                        <td className="py-2 text-right">
                          <span className="font-semibold text-gray-900">{link.clicks}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </Card>
      ))}
    </div>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────

export const NewsletterManagementPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<string | number>('subscribers');

  const handleUseTemplate = (t: NewsletterTemplate) => {
    // Switch to subscribers tab — the send modal will pick up the template there
    // We surface it via a shared query so SubscribersTab can pre-fill
    queryClient.setQueryData(['newsletter-pending-template'], t);
    setActiveTab('subscribers');
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Newsletter Management</h1>
        <p className="text-gray-600 mt-1">Manage subscribers, create templates, and send campaigns</p>
      </div>

      <Tabs
        activeTab={activeTab}
        onChange={setActiveTab}
        tabs={[
          {
            id: 'subscribers',
            label: 'Subscribers',
            content: <SubscribersTab />,
          },
          {
            id: 'platform-users',
            label: 'Free / Trial Users',
            content: <PlatformUsersTab />,
          },
          {
            id: 'templates',
            label: 'Letter Templates',
            content: <TemplatesTab onUseTemplate={handleUseTemplate} />,
          },
          {
            id: 'analytics',
            label: 'Click Analytics',
            content: <ClickAnalyticsTab />,
          },
        ]}
      />
    </div>
  );
};

export default NewsletterManagementPage;
